import { useState, useEffect } from "react";
import { TeamMembersPanel } from "@/components/team/TeamMembersPanel";
import { TeamBillingPanel } from "@/components/team/TeamBillingPanel";
import { TeamBillingReadOnly } from "@/components/team/TeamBillingReadOnly";
import { useTeam } from "@/hooks/useTeam";
import { useNavigate } from "react-router-dom";
import { X, User, Palette, CreditCard, Users, Database, Pen, Flame, FileCheck2, Info, Check, Sun, Moon, ChevronDown, Zap, Lock, Sparkles, Calendar, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { useTheme } from "@/components/ThemeProvider";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage, useTranslation } from "@/context/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { format, differenceInDays } from "date-fns";
import { USAGE_INTENTS } from "@/data/onboardingOptions";
import { useUsageLimits } from "@/hooks/useUsageLimits";
import { LANGUAGES } from "@/data/languages";
// Themes config
const themes = [{
  code: "light",
  label: "Light",
  icon: Sun
}, {
  code: "dark",
  label: "Dark",
  icon: Moon
}];
import { MODELS } from "@/lib/ai/models";
interface SettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStateChange?: (state: any) => void;
  onTabChange?: (tab: string) => void;
}
export const SettingsDialog = ({
  open,
  onOpenChange,
  onStateChange,
  onTabChange
}: SettingsDialogProps) => {
  const [activeTab, setActiveTab] = useState("account");
  const { language: selectedLanguage, setLanguage: setSelectedLanguage, isRtl } = useLanguage();
  const { t, formatNumber } = useTranslation();
  const {
    theme,
    setTheme
  } = useTheme();
  const [selectedChatModel, setSelectedChatModel] = useState("auto");
  
  // Popover open states for auto-close on selection
  const [languagePopoverOpen, setLanguagePopoverOpen] = useState(false);
  const [themePopoverOpen, setThemePopoverOpen] = useState(false);
  const [chatModelPopoverOpen, setChatModelPopoverOpen] = useState(false);
  const {
    toast
  } = useToast();
  const {
    user
  } = useAuth();
  const navigate = useNavigate();
  const teamData = useTeam();

  // User data states
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<{
    skills: string[] | null;
    profile_role: string | null;
    intent: string | null;
    created_at: string;
    current_streak: number;
    subscription_status: string | null;
    subscription_plan: string | null;
    subscription_period_end: string | null;
    payment_method_last4: string | null;
    invite_code: string | null;
    school: string | null;
    courses: string[] | null;
  } | null>(null);

  // Usage limits hook
  const usageLimits = useUsageLimits();
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    onTabChange?.(tab);
  };
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [formData, setFormData] = useState({
    name: "Azzam Ali S",
    purpose: "Work",
    role: "Entrepreneur"
  });
  const handleCopyReferralLink = () => {
    const inviteCode = userProfile?.invite_code || "";
    const referralLink = inviteCode ? `${window.location.origin}/signup?ref=${inviteCode}` : "";
    if (!referralLink) {
      toast({
        title: "No referral link",
        description: "Unable to generate referral link. Please try again later.",
        variant: "destructive"
      });
      return;
    }
    navigator.clipboard.writeText(referralLink);
    toast({
      title: "Link copied!",
      description: "Your referral link has been copied to clipboard."
    });
  };
  const handleEdit = (field: string, currentValue: string) => {
    setEditingField(field);
    setEditValue(currentValue);
  };
  const handleSave = async (field: string) => {
    if (field === "name" && user) {
      try {
        // Update auth user metadata
        const {
          error: authError
        } = await supabase.auth.updateUser({
          data: {
            full_name: editValue
          }
        });
        if (authError) throw authError;

        // Also persist to profiles table
        await supabase
          .from("profiles")
          .update({ name: editValue.trim() })
          .eq("id", user.id);

        toast({
          title: "Success",
          description: "Your name has been updated successfully."
        });

        // Refresh user data
        const {
          data: {
            session
          }
        } = await supabase.auth.getSession();
        if (session) {
          setEditingField(null);
        }
      } catch (error: any) {
        toast({
          title: "Error",
          description: error.message || "Failed to update name",
          variant: "destructive"
        });
      }
    } else {
      const newData = {
        ...formData,
        [field]: editValue
      };
      setFormData(newData);
      setEditingField(null);
      onStateChange?.(newData);
    }
  };
  const handleCancel = () => {
    setEditingField(null);
    setEditValue("");
  };

  // Fetch user profile and content count
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        // Fetch profile data
        const {
          data: profile
        } = await supabase.from("profiles").select("skills, profile_role, intent, created_at, current_streak, subscription_status, subscription_plan, subscription_period_end, payment_method_last4, invite_code, school, courses").eq("id", user.id).single();
        if (profile) {
          setUserProfile(profile);
        }

      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    };
    if (open) {
      fetchUserData();
    }

    // Set up realtime subscription for profile changes
    const profileChannel = supabase.channel("settings-profile-changes").on("postgres_changes", {
      event: "UPDATE",
      schema: "public",
      table: "profiles",
      filter: `id=eq.${user?.id}`
    }, payload => {
      setUserProfile(prev => ({
        ...prev!,
        subscription_status: payload.new.subscription_status || prev?.subscription_status || null,
        subscription_plan: payload.new.subscription_plan || prev?.subscription_plan || null,
        subscription_period_end: payload.new.subscription_period_end || prev?.subscription_period_end || null,
        payment_method_last4: payload.new.payment_method_last4 || prev?.payment_method_last4 || null
      }));
    }).subscribe();
    return () => {
      supabase.removeChannel(profileChannel);
    };
  }, [user, open]);

  // Get user's display name
  const getUserName = () => {
    if (!user) return formData.name;
    return user.user_metadata?.full_name || user.email?.split("@")[0] || formData.name;
  };

  // Get user's email
  const getUserEmail = () => {
    if (!user) return "azzamalis@icloud.com";
    return user.email || "azzamalis@icloud.com";
  };

  // Get date created
  const getDateCreated = () => {
    if (!userProfile) return "May 18, 2025";
    try {
      return format(new Date(userProfile.created_at), "MMMM dd, yyyy");
    } catch {
      return "May 18, 2025";
    }
  };
  const tabs = [
    { id: "account", labelKey: "settings.account", icon: User },
    { id: "personalization", labelKey: "settings.personalization", icon: Palette },
    { id: "billing", labelKey: "settings.planBilling", icon: CreditCard },
    ...(teamData.team ? [{ id: "members", labelKey: "settings.members", icon: Users }] : []),
    { id: "data", labelKey: "settings.dataControls", icon: Database },
  ];

  // Reset active tab if user lost team access while on members tab
  useEffect(() => {
    if (activeTab === "members" && !teamData.team && !teamData.loading) {
      setActiveTab("billing");
    }
  }, [activeTab, teamData.team, teamData.loading]);

  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-3xl h-[85vh] max-h-[85vh] md:h-[600px] md:max-h-[600px] p-0 overflow-hidden [&>button]:hidden" dir={isRtl ? "rtl" : "ltr"}>
        <div className="flex flex-col md:flex-row h-full overflow-hidden">
          {/* Sidebar - Desktop */}
          <div className={`hidden md:block w-48 flex-shrink-0 bg-muted/50 border-r p-2 ${isRtl ? "border-l border-r-0" : ""}`}>
            <Button variant="ghost" size="icon" className={`${isRtl ? "mr-0.5" : "ml-0.5"} mb-1 h-8 w-8`} onClick={() => onOpenChange(false)}>
              <X className="h-4 w-4" />
            </Button>
            <nav className="space-y-1">
              {tabs.map(tab => <button key={tab.id} onClick={() => handleTabChange(tab.id)} className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors ${activeTab === tab.id ? "bg-muted text-foreground font-medium" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"}`}>
                  <tab.icon className="h-4 w-4" />
                  {t(tab.labelKey)}
                </button>)}
            </nav>
          </div>

          {/* Mobile Tabs */}
          <div className="block md:hidden flex-shrink-0 border-b px-3 py-2">
            <div className="flex justify-between items-center mb-2">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onOpenChange(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="w-full overflow-hidden">
              <nav className="flex gap-1 overflow-x-auto pb-2 -mx-1 px-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
                {tabs.map(tab => <button key={tab.id} onClick={() => handleTabChange(tab.id)} className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg transition-colors whitespace-nowrap ${activeTab === tab.id ? "bg-muted text-foreground font-medium" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"}`}>
                    <tab.icon className="h-3 w-3" />
                    {t(tab.labelKey)}
                  </button>)}
              </nav>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-h-0 p-4 md:p-6 overflow-y-auto">
            {activeTab === "account" && <div className="space-y-0">
                {loading ? <div className="flex items-center justify-center py-8">
                    <div className="text-sm text-muted-foreground">{t("common.loading")}</div>
                  </div> : <>
                    <SettingRow label={t("settings.name")} value={getUserName()} editable isEditing={editingField === "name"} editValue={editValue} onEdit={() => handleEdit("name", getUserName())} onSave={() => handleSave("name")} onCancel={handleCancel} onValueChange={setEditValue} />
                    <SettingRow label={t("settings.email")} value={getUserEmail()} />
                    <SettingRow label={t("settings.dateCreated")} value={getDateCreated()} />
                    <SettingRow label={t("settings.purpose")} value={USAGE_INTENTS.find(i => i.value === userProfile?.intent)?.label || userProfile?.intent || "Not specified"} />
                    {(userProfile?.intent === "study" || userProfile?.intent === "teacher") ? (
                      <>
                        <SettingRow label={t("settings.school")} value={userProfile?.school || "Not specified"} />
                        <div className="flex items-center justify-between py-3 border-b">
                          <span className="text-sm font-medium">{t("settings.courses")}</span>
                          <div className="flex gap-1 flex-wrap justify-end max-w-xs">
                            {userProfile?.courses && userProfile.courses.length > 0 ? userProfile.courses.map((course, index) => <Badge key={index} variant="secondary">{course}</Badge>) : <span className="text-sm text-muted-foreground">{t("settings.noCoursesAdded")}</span>}
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <SettingRow label={t("settings.role")} value={userProfile?.profile_role || "Not specified"} />
                        <div className="flex items-center justify-between py-3 border-b">
                          <span className="text-sm font-medium">{t("settings.skills")}</span>
                          <div className="flex gap-1 flex-wrap justify-end max-w-xs">
                            {userProfile?.skills && userProfile.skills.length > 0 ? userProfile.skills.map((skill, index) => <Badge key={index} variant="secondary">{skill}</Badge>) : <span className="text-sm text-muted-foreground">{t("settings.noSkillsAdded")}</span>}
                          </div>
                        </div>
                      </>
                    )}
                    <div className="flex items-center justify-between py-3 border-b">
                      <span className="text-sm font-medium">{t("settings.streaks")}</span>
                      <div className="flex items-center gap-2">
                        <Flame className="h-4 w-4 text-warning" />
                        <span className="text-sm text-muted-foreground">
                          {formatNumber(userProfile?.current_streak || 0)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between py-3 border-b">
                      <span className="text-sm font-medium">{t("settings.contentCount")}</span>
                      <div className="flex items-center gap-2">
                        <FileCheck2 className="h-4 w-4 text-info" />
                        <span className="text-sm text-muted-foreground">{formatNumber(usageLimits.contentUploads.used)}</span>
                      </div>
                    </div>
                  </>}
                
                {/* Referral Section */}
                <div className="pt-4 border-t mt-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex flex-col gap-2 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold">{t("settings.referralLink")}</span>
                      </div>
                      <div className="text-xs text-muted-foreground flex items-center">
                        {t("settings.referralDescription")}
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className={`h-3.5 w-3.5 cursor-help ${isRtl ? "mr-1" : "ml-1"}`} />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-xs">{t("settings.referralTooltip")}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                    <Button onClick={handleCopyReferralLink}>{t("common.copyLink")}</Button>
                  </div>
                </div>
              </div>}
            
            {activeTab === "personalization" && <div className="space-y-0">
                <div className="flex items-center justify-between py-3 border-b">
                  <span className="text-sm font-medium">{t("settings.language")}</span>
                  <Popover open={languagePopoverOpen} onOpenChange={setLanguagePopoverOpen}>
                    <PopoverTrigger asChild>
                      <button className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                        {LANGUAGES.find(l => l.code === selectedLanguage)?.flag} {LANGUAGES.find(l => l.code === selectedLanguage)?.label}
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-56 p-1 bg-popover border shadow-xl rounded-lg max-h-60 overflow-y-auto">
                      {LANGUAGES.filter(lang => ["english", "arabic", "french", "spanish", "turkish"].includes(lang.code)).map(lang => <div key={lang.code} onClick={() => { setSelectedLanguage(lang.code); setLanguagePopoverOpen(false); }} className="px-3 py-2 text-sm hover:bg-accent rounded-md cursor-pointer transition-colors text-primary/70 flex items-center gap-2">
                          <span className="text-lg">{lang.flag}</span>
                          <span className="capitalize">{lang.label}</span>
                        </div>)}
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex items-center justify-between py-3 border-b">
                  <span className="text-sm font-medium">{t("settings.theme")}</span>
                  <Popover open={themePopoverOpen} onOpenChange={setThemePopoverOpen}>
                    <PopoverTrigger asChild>
                      <button className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                        {(() => {
                      const currentTheme = themes.find(thm => thm.code === theme);
                      const Icon = currentTheme?.icon || Sun;
                      return <Icon className="h-4 w-4" />;
                    })()}
                        {theme === "light" ? t("settings.light") : theme === "dark" ? t("settings.dark") : t("settings.auto")}
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-40 p-1 bg-popover border shadow-xl rounded-lg">
                      {themes.map(themeOption => {
                    const Icon = themeOption.icon;
                    const themeLabel = themeOption.code === "light" ? t("settings.light") : t("settings.dark");
                    return <div key={themeOption.code} onClick={() => { setTheme(themeOption.code as "light" | "dark"); setThemePopoverOpen(false); }} className="px-3 py-2 text-sm hover:bg-accent rounded-md cursor-pointer transition-colors text-primary/70 flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            <span>{themeLabel}</span>
                          </div>;
                  })}
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex items-center justify-between py-3 border-b">
                  <span className="text-sm font-medium">{t("settings.chatModel")}</span>
                  <Popover open={chatModelPopoverOpen} onOpenChange={setChatModelPopoverOpen}>
                    <PopoverTrigger asChild>
                      <button className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                        {MODELS.find(m => m.value === selectedChatModel)?.label ?? "Auto"}
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-56 p-1 bg-popover border shadow-xl rounded-lg max-h-60 overflow-y-auto">
                      {MODELS.filter(m => !m.hidden).map(model => <div key={model.value} onClick={() => { if (model.tier === "pro" && userProfile?.subscription_status !== "active") { setChatModelPopoverOpen(false); onOpenChange(false); navigate("/pricing"); return; } setSelectedChatModel(model.value); setChatModelPopoverOpen(false); }} className="px-3 py-2 text-sm hover:bg-accent rounded-md cursor-pointer transition-colors text-primary/70 flex items-center justify-between gap-2">
                          <span>{model.label}</span>
                          {model.tier === "pro" && userProfile?.subscription_status !== "active" && <Badge className="text-xs px-1.5 py-0 h-5 text-success bg-success/10 border-success/20 pointer-events-none">
                              {t("settings.upgrade")}
                            </Badge>}
                        </div>)}
                    </PopoverContent>
                  </Popover>
                </div>
              </div>}
            
            {activeTab === "billing" && (loading ? <div className="flex items-center justify-center py-12">
                <div className="text-sm text-muted-foreground">Loading subscription data...</div>
              </div> : teamData?.team && teamData?.isOwner ? <TeamBillingPanel /> : teamData?.team && !teamData?.isOwner ? <TeamBillingReadOnly /> : <ScrollArea className="h-[calc(85vh-10rem)] md:h-[calc(600px-8rem)] pr-2 md:pr-4">
                <div className="space-y-0">
                {/* Check real subscription status */}
                {userProfile?.subscription_status === 'active' ?
              // Pro Plan UI
              <>
                    <div className="flex items-center justify-between py-3 border-b">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-medium">Pro Plan</h3>
                          <Badge variant="default" className="bg-primary">Active</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Full access to all premium features
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold">
                          {userProfile?.subscription_plan === 'yearly' ? <>
                              <span>$290</span>
                              <span className="text-sm text-muted-foreground font-normal"> /year</span>
                            </> : <>
                              <span>SAR 75</span>
                              <span className="text-sm text-muted-foreground font-normal"> /month</span>
                            </>}
                        </p>
                      </div>
                    </div>

                    {/* Subscription Details Section */}
                    <div className="pt-4 pb-2">
                      <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Subscription Details</h4>
                    </div>
                    <div className="space-y-0">
                      <div className="flex items-center justify-between py-3 border-b">
                        <span className="text-sm font-medium">Next Billing Date</span>
                        <span className="text-sm text-muted-foreground">
                          {userProfile?.subscription_period_end ? new Date(userProfile.subscription_period_end).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      }) : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                        </span>
                      </div>
                      <div className="flex items-center justify-between py-3 border-b">
                        <span className="text-sm font-medium">Payment Method</span>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CreditCard className="h-4 w-4" />
                          <span>•••• {userProfile?.payment_method_last4 || '4242'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Usage Statistics Section */}
                    <div className="pt-4 pb-2">
                      <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Usage & Limits</h4>
                    </div>
                    <div className="space-y-0">
                      <div className="flex items-center justify-between py-3 border-b">
                        <div className="flex items-center gap-2">
                          
                          <span className="text-sm font-medium">Uploads</span>
                        </div>
                        <div className="text-right">
                          
                          <span className="text-xs text-muted-foreground ml-2 font-extrabold">Unlimited</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between py-3 border-b">
                        <div className="flex items-center gap-2">
                          
                          <span className="text-sm font-medium">AI Chats</span>
                        </div>
                        <div className="text-right">
                          
                          <span className="text-xs text-muted-foreground ml-2 font-extrabold">Unlimited</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between py-3 border-b">
                        <div className="flex items-center gap-2">
                          
                          <span className="text-sm font-medium">Exams</span>
                        </div>
                        <div className="text-right">
                          
                          <span className="text-xs text-muted-foreground ml-2 font-extrabold">Unlimited</span>
                        </div>
                      </div>
                    </div>

                    {/* Billing History Section */}
                    <div className="pt-4 pb-2">
                      <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Billing History</h4>
                    </div>
                    <div className="space-y-0">
                      <div className="flex items-center justify-between py-3 border-b">
                        <span className="text-sm font-medium">Date</span>
                        <span className="text-sm font-medium">Amount</span>
                        <span className="text-sm font-medium">Status</span>
                        <span className="text-sm font-medium">Invoice</span>
                      </div>
                      <div className="flex items-center justify-between py-3 border-b">
                        <span className="text-sm text-muted-foreground">
                          {new Date().toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {userProfile?.subscription_plan === 'monthly' ? 'SAR 75' : '$29'} / month
                        </span>
                        <Badge variant="outline" className="bg-success/10 text-success border-success/30 hover:bg-success/10">Paid</Badge>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="flex items-center justify-between py-3 border-b">
                        <span className="text-sm text-muted-foreground">
                          {new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {userProfile?.subscription_plan === 'monthly' ? 'SAR 75' : '$29'} / month
                        </span>
                        <Badge variant="outline" className="bg-success/10 text-success border-success/30 hover:bg-success/10">Paid</Badge>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Plan Management */}
                    <div className="pt-4 pb-2">
                      <h3 className="text-lg font-semibold">Manage Plan</h3>
                    </div>
                    <div className="py-3">
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Button variant="default" className="whitespace-nowrap">
                          {userProfile?.subscription_plan === 'yearly' ? 'Switch to Monthly' : 'Switch to Yearly'}
                          {userProfile?.subscription_plan !== 'yearly' && <Badge variant="secondary" className="ml-2 shrink-0 pointer-events-none">Save 40%</Badge>}
                        </Button>
                        <Button variant="default" className="whitespace-nowrap">
                          Update Payment
                        </Button>
                      </div>
                      
                    </div>

                    {/* Pro Features Reminder */}
                    
                  </> :
              // Free Plan UI
              <>
                    <div className="flex items-center justify-between py-3 border-b">
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-medium">{t("settings.freePlan")}</span>
                        <span className="text-xs text-muted-foreground">{t("settings.limitedFeatures")}</span>
                      </div>
                      <Button onClick={() => {
                    onOpenChange(false);
                    navigate("/pricing");
                  }}>
                        {t("settings.upgradeToPro")}
                      </Button>
                    </div>

                    <div className="py-3 border-b">
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">Content Uploads</span>
                            </div>
                            <span className="text-muted-foreground">{usageLimits.contentUploads.used} / {usageLimits.contentUploads.limit} per day</span>
                          </div>
                          <Progress value={(usageLimits.contentUploads.used / usageLimits.contentUploads.limit) * 100} className="h-2" />
                          <p className="text-xs text-muted-foreground">Upload PDFs, videos, and documents</p>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">Flashcards</span>
                            </div>
                            <span className="text-muted-foreground">{usageLimits.flashcards.used} / {usageLimits.flashcards.limit} per day</span>
                          </div>
                          <Progress value={(usageLimits.flashcards.used / usageLimits.flashcards.limit) * 100} className="h-2" />
                          <p className="text-xs text-muted-foreground">Generate and practice with flashcards</p>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">Chat Messages</span>
                            </div>
                            <span className="text-muted-foreground">{usageLimits.chatMessages.used} / {usageLimits.chatMessages.limit} per day</span>
                          </div>
                          <Progress value={(usageLimits.chatMessages.used / usageLimits.chatMessages.limit) * 100} className="h-2" />
                          <p className="text-xs text-muted-foreground">Ask Shattara AI questions about your content</p>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">Quizzes Q&A </span>
                            </div>
                            <span className="text-muted-foreground">{usageLimits.quizAnswers.used} / {usageLimits.quizAnswers.limit} per day</span>
                          </div>
                          <Progress value={(usageLimits.quizAnswers.used / usageLimits.quizAnswers.limit) * 100} className="h-2" />
                          <p className="text-xs text-muted-foreground">Answer content-generated quiz questions</p>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">Practice Exams</span>
                            </div>
                            <span className="text-muted-foreground">{usageLimits.practiceExams.used} / {usageLimits.practiceExams.limit} per month</span>
                          </div>
                          <Progress value={(usageLimits.practiceExams.used / usageLimits.practiceExams.limit) * 100} className="h-2" />
                          <p className="text-xs text-muted-foreground">Generate full practice exams</p>
                        </div>
                      </div>
                    </div>
                  </>}
              </div>
              </ScrollArea>)}
            
            {activeTab === "members" && <TeamMembersPanel />}
            
            {activeTab === "data" && <DataControlsTab onClose={() => onOpenChange(false)} />}
          </div>
        </div>
      </DialogContent>
    </Dialog>;
};
const SettingRow = ({
  label,
  value,
  editable = false,
  isEditing = false,
  editValue = "",
  onEdit,
  onSave,
  onCancel,
  onValueChange
}: {
  label: string;
  value: string;
  editable?: boolean;
  isEditing?: boolean;
  editValue?: string;
  onEdit?: () => void;
  onSave?: () => void;
  onCancel?: () => void;
  onValueChange?: (value: string) => void;
}) => <div className="flex items-center justify-between py-3 border-b">
    <span className="text-sm font-medium">{label}</span>
    <div className="flex items-center gap-2">
      {isEditing ? <>
          <Input value={editValue} onChange={e => onValueChange?.(e.target.value)} className="h-8 w-48 text-sm" autoFocus />
          <Button variant="ghost" size="icon" className="h-7 w-7 text-correct hover:text-correct/80 hover:bg-correct/10" onClick={onSave}>
            <Check className="h-3 w-3" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-incorrect hover:text-incorrect/80 hover:bg-incorrect/10" onClick={onCancel}>
            <X className="h-3 w-3" />
          </Button>
        </> : <>
          <span className="text-sm text-muted-foreground">{value}</span>
          {editable && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onEdit}>
              <Pen className="h-3 w-3" />
            </Button>}
        </>}
    </div>
  </div>;

// Data Controls Tab with Delete Account functionality
const DataControlsTab = ({ onClose }: { onClose: () => void }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t, isRtl } = useTranslation();

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Error",
          description: "You must be logged in to delete your account",
          variant: "destructive"
        });
        return;
      }

      const response = await supabase.functions.invoke("delete-account", {
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (response.error) {
        throw new Error(response.error.message || "Failed to delete account");
      }

      // Sign out the user
      await supabase.auth.signOut();

      toast({
        title: "Account Deleted",
        description: "Your account has been permanently deleted."
      });

      // Close dialog and navigate to home
      onClose();
      navigate("/");
    } catch (error: any) {
      console.error("Error deleting account:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete account. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
      setIsDialogOpen(false);
    }
  };

  return (
    <div className="flex items-center justify-between py-3 border-b">
      <span className="text-sm font-medium">{t("settings.deleteAccount")}</span>
      <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <AlertDialogTrigger asChild>
          <Button variant="destructive" disabled={isDeleting}>
            {isDeleting ? t("settings.deleting") : t("settings.delete")}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent dir={isRtl ? "rtl" : "ltr"}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("settings.areYouSure")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("settings.cannotUndo")}
              <ul className={`list-disc mt-2 space-y-1 ${isRtl ? "mr-4" : "ml-4"}`}>
                <li>{t("settings.deleteListUploaded")}</li>
                <li>{t("settings.deleteListFlashcards")}</li>
                <li>{t("settings.deleteListQuizzes")}</li>
                <li>{t("settings.deleteListChat")}</li>
                <li>{t("settings.deleteListProfile")}</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className={isRtl ? "flex-row-reverse" : ""}>
            <AlertDialogCancel disabled={isDeleting}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(e) => {
                e.preventDefault();
                handleDeleteAccount();
              }}
              disabled={isDeleting}
            >
              {isDeleting ? t("settings.deleting") : t("settings.deleteAccount")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};